import { Fragment, useState, useCallback, useEffect, useMemo } from "react";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import { Card, CardBody, CardHeader, Col, Form, Row, Button, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { ArrowLeft, Search, PlusCircle } from "react-feather";
import AsyncSelect from "react-select/async";
import Select, { components } from "react-select";
import { suppliersMasterAPI } from "../PharmacySuppliers/SupplierService";
import { createPreOrderAPI, getPreOrderItemsAPI } from "./PreorderService";
import { getPharmacyItems } from "../PharmacyMedicineAdd/PharmacyMedicineService";
import { toast } from "react-toastify";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { Input, Label } from "reactstrap";

const AddPreOrder = () => {
    const { t } = useTranslation();
    const history = useNavigate();
    const { loading, setLoading } = useLoader();
    const { pharmacyPOS } = usePharmacy();
    const disableScrollProps = useDisableScrollOnFocus();

    // ============ FORM STATE ============
    const [supplier, setSupplier] = useState(null);
    const [recentSuppliers, setRecentSuppliers] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchQuerySupplier, setSearchQuerySupplier] = useState('');
    const [searchQueryItem, setSearchQueryItem] = useState('');
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
    const [allItems, setAllItems] = useState([]); // Store all fetched items

    // ============ FORM DATA ============
    const [formData, setFormData] = useState({
        notes: ""
    });

    const [errors, setErrors] = useState({});

    const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);

    // ============ LOAD SUPPLIERS ============
    const loadSuppliers = async (inputValue) => {
        // if (!inputValue) return [];
        try {
            const res = await suppliersMasterAPI({
                search: inputValue,
                clinicId: pharmacyPOS?.hospital_id
            });
            const suppliers = (res.data.data || []).map(s => ({
                value: s._id,
                label: `${s.companyName} [${s.contactPerson || 'No Contact'}]`,
                raw: s
            }));
            setRecentSuppliers(suppliers.slice(0, 5));
            return suppliers;
        } catch (err) {
            console.error("Error loading suppliers:", err);
            return [];
        }
    };

    // ============ LOAD ALL ITEMS ============
    const loadAllItems = useCallback(async () => {
        try {
            setIsLoadingItems(true);
            setAllItems([]); // Clear previous items

            // Fetch all items (no search filter)
            const response = await getPharmacyItems({
                search: "", // Empty search to get all items
                clinicId: pharmacyPOS?.hospital_id
            });

            const items = response.data.data || [];

            // Format items for display
            const formattedItems = items.map(item => ({
                value: item._id,
                label: item.name ? item.name : `${item.brandName || ""} [${item.saltName || ""}]`,
                raw: {
                    ...item,
                    brandName: item.name || item.brandName,
                    saltName: item.saltName || "",
                    manufacturer: item.manufacturer || "",
                    package: item.package || "",
                    productForm: item.productForm || "",
                    price: item.regularPrice || 0
                }
            }));

            setAllItems(formattedItems);

        } catch (error) {
            console.error("Error loading items:", error);
            toast.warning(t("Failed to load items"));
            return [];
        } finally {
            setIsLoadingItems(false);
        }
    }, [pharmacyPOS?.hospital_id, t]);

    // Load all items on component mount
    useEffect(() => {
        loadAllItems();
    }, [loadAllItems]);

    useEffect(() => {
        loadSuppliers();
    }, []);

    // ============ FILTER ITEMS BASED ON SEARCH ============
    const filterItems = useMemo(() => {
        if (!searchQueryItem) {
            return allItems;
        }

        const searchLower = searchQueryItem.toLowerCase();
        return allItems.filter(item =>
            item.label.toLowerCase().includes(searchLower) ||
            item.raw.brandName?.toLowerCase().includes(searchLower) ||
            item.raw.saltName?.toLowerCase().includes(searchLower) ||
            item.raw.manufacturer?.toLowerCase().includes(searchLower)
        );
    }, [allItems, searchQueryItem]);

    // ============ CUSTOM SELECT COMPONENTS ============
    const Option = (props) => {
        return (
            <components.Option {...props}>
                <div className="d-flex align-items-center py-1">
                    <input
                        type="checkbox"
                        checked={props.isSelected}
                        onChange={() => null}
                        className="me-2"
                        style={{ pointerEvents: 'none' }}
                    />
                    <label className="m-0 cursor-pointer w-100">{props.label}</label>
                </div>
            </components.Option>
        );
    };

    // Select All Option
    const allOption = { label: t("Select All"), value: "*" };

    // Custom styles for react-select
    const customStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '44px',
            height: '44px',
            overflow: 'hidden',
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: '44px',
            padding: '0 8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
                height: '6px',
            },
            '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '10px',
            },
        }),
        multiValue: (styles) => ({
            ...styles,
            backgroundColor: '#1b255b',
            margin: '2px',
            flexShrink: 0,
        }),
        multiValueLabel: (styles) => ({
            ...styles,
            color: '#fff',
            fontWeight: 500,
        }),
        multiValueRemove: (styles) => ({
            ...styles,
            color: '#d32f2f',
            ':hover': {
                backgroundColor: '#ffcdd2',
                color: 'white',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            overflowY: 'hidden'
        }),
    };

    // ============ GET SELECT OPTIONS ============
    const getSelectOptions = useMemo(() => {
        return filterItems.length > 0 ? [allOption, ...filterItems] : [];
    }, [filterItems, allOption]);

    // ============ GET SELECTED VALUES ============
    const getSelectedValues = useMemo(() => {
        return selectedItems.map(item => ({
            value: item.id,
            label: item.brandName,
            raw: item
        }));
    }, [selectedItems]);

    // ============ HANDLE ITEM SELECTION ============
    const handleItemSelection = (selectedOptions) => {
        if (!selectedOptions) {
            setSelectedItems([]);
            return;
        }

        const isSelectAll = selectedOptions.some(option => option.value === "*");

        if (isSelectAll) {
            // If "Select All" is selected
            const allSelected = filterItems.length > 0 ? filterItems : allItems;
            const updatedSelectedItems = allSelected.map(option => {
                const existingItem = selectedItems.find(item => item.id === option.value);

                if (existingItem) {
                    return existingItem;
                } else {
                    return {
                        id: option.value,
                        brandName: option.raw.brandName || "",
                        saltName: option.raw.saltName || "",
                        manufacturer: option.raw.manufacturer || "",
                        package: option.raw.package || "",
                        productForm: option.raw.productForm || "",
                        itemId: option.value,
                        quantity: 1,
                        price: option.raw.price || 0,
                        notes: ""
                    };
                }
            });

            setSelectedItems(updatedSelectedItems);
        } else {
            // Regular selection
            const updatedSelectedItems = selectedOptions.map(option => {
                const existingItem = selectedItems.find(item => item.id === option.value);

                if (existingItem) {
                    return existingItem;
                } else {
                    return {
                        id: option.value,
                        brandName: option.raw.brandName || "",
                        saltName: option.raw.saltName || "",
                        manufacturer: option.raw.manufacturer || "",
                        package: option.raw.package || "",
                        productForm: option.raw.productForm || "",
                        itemId: option.value,
                        quantity: 1,
                        price: option.raw.price || 0,
                        notes: ""
                    };
                }
            });

            setSelectedItems(updatedSelectedItems);
        }
    };

    // ============ SELECT SUPPLIER ============
    const handleSelectSupplier = (option) => {
        setSupplier(option);
        setErrors(prev => ({ ...prev, supplier: null }));
    };

    // ============ REMOVE ITEM ============
    const handleRemoveItem = (itemId) => {
        setSelectedItems(prev => prev.filter(item => item.id !== itemId));
    };

    // ============ UPDATE QUANTITY ============
    const handleQuantityChange = (itemId, value) => {
        const quantity = Math.max(1, parseInt(value) || 1);
        setSelectedItems(prev =>
            prev.map(item =>
                item.id === itemId ? { ...item, quantity } : item
            )
        );
    };

    // ============ UPDATE NOTES ============
    const handleNotesChange = (itemId, value) => {
        setSelectedItems(prev =>
            prev.map(item =>
                item.id === itemId ? { ...item, notes: value } : item
            )
        );
    };

    // ============ ADD ALL MIN STOCK ITEMS ============
    const handleAddAllMinStock = async () => {
        try {
            setLoading(true);

            if (!supplier) {
                toast.warning(t("Please select a supplier first"));
                return;
            }

            const response = await getPreOrderItemsAPI({
                supplierId: supplier.value,
            });

            const minStockItems = response.data.data || [];

            if (minStockItems.length === 0) {
                toast.info(t("No items below minimum stock level"));
                return;
            }

            const newItems = minStockItems.map(item => ({
                id: item._id,
                brandName: item.brandName || "",
                saltName: item.saltName || "",
                manufacturer: item.manufacturer || "",
                package: item.package || "",
                productForm: item.productForm || "",
                itemId: item._id,
                quantity: Math.max(1, item.minQuantityAlert - (item.stockQuantity || 0)),
                price: item.regularPrice || 0,
                notes: `Min stock: ${item.minQuantityAlert}, Current: ${item.stockQuantity || 0}`
            }));

            const existingIds = new Set(selectedItems.map(i => i.id));
            const itemsToAdd = newItems.filter(item => !existingIds.has(item.id));

            setSelectedItems(prev => [...prev, ...itemsToAdd]);

            toast.success(t(`Added ${itemsToAdd.length} items below minimum stock level`));

        } catch (error) {
            console.error("Error adding min stock items:", error);
            toast.warning(error.response?.data?.message || t("Failed to add min stock items"));
        } finally {
            setLoading(false);
        }
    };

    // ============ SUBMIT FORM ============
    const handleAddPreOrder = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!supplier) {
            newErrors.supplier = t("Supplier is required");
        }
        if (selectedItems.length === 0) {
            newErrors.items = t("Please select at least one item");
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.warning(t("Please fill in all required fields"));
            return;
        }

        try {
            setLoading(true);

            const payload = {
                pharmacyId: pharmacyPOS?._id,
                clinicId: pharmacyPOS?.hospital_id,
                supplierId: supplier.value,
                preorderData: selectedItems.map(item => ({
                    brandName: item.brandName,
                    saltName: item.saltName,
                    manufacturer: item.manufacturer,
                    package: item.package,
                    productForm: item.productForm,
                    itemId: item.itemId,
                    quantity: item.quantity,
                    notes: item.notes
                }))
            };

            const response = await createPreOrderAPI(payload);
            toast.success(response.data.message || t("Pre-order created successfully"));
            resetForm();

            setTimeout(() => {
                history(`${process.env.PUBLIC_URL}/pre-order-list`);
            }, 1500);

        } catch (error) {
            console.error("Error creating pre-order:", error);
            toast.warning(error.response?.data?.message || t("Failed to create pre-order"));
        } finally {
            setLoading(false);
        }
    };

    // ============ RESET FORM ============
    const resetForm = () => {
        setSupplier(null);
        setSelectedItems([]);
        setFormData({ notes: "" });
        setErrors({});
        setSearchQuerySupplier('');
        setSearchQueryItem('');
    };

    // ============ NAVIGATE ============
    const gotoList = () => {
        resetForm();
        history(`${process.env.PUBLIC_URL}/pre-order-list`);
    };

    const isDark = document.body.classList.contains("dark-only");
    return (
        <Fragment>
            <Breadcrumbs
                mainTitle={t("Create Order")}
                parent={t("Dashboard")}
                title={t("Create Order")}
            />
            <Row className="mt-3">
                <Col sm={12}>
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">{t("Create Order")}</h5>
                            <div className="d-flex gap-2 align-items-center">
                                {supplier && (
                                    <button
                                        className="btn btn-secondary d-flex align-items-center"
                                        style={{ color: "white", height: "40px" }}
                                        onClick={handleAddAllMinStock}
                                        disabled={loading}
                                        title={t("Add all items below minimum stock level from this supplier")}
                                    >
                                        <PlusCircle className="me-2" size={18} />
                                        {t("Add Min Stock Items")}
                                    </button>
                                )}
                                <button
                                    className="btn btn-primary d-flex align-items-center"
                                    style={{ color: "white", height: "40px" }}
                                    onClick={gotoList}
                                    disabled={loading}
                                >
                                    <ArrowLeft className="me-2" /> {t("Back to List")}
                                </button>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <Form onSubmit={handleAddPreOrder}>
                                <Row className="g-3 mb-4">
                                    {/* Supplier Selection */}
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("Supplier")} <span className="text-danger">*</span></Form.Label>
                                            <AsyncSelect
                                                className="flex-grow-1"
                                                cacheOptions
                                                loadOptions={loadSuppliers}
                                                defaultOptions={recentSuppliers}
                                                onChange={handleSelectSupplier}
                                                onInputChange={setSearchQuerySupplier}
                                                placeholder={t("Search & select a supplier")}
                                                noOptionsMessage={() =>
                                                    searchQuerySupplier
                                                        ? t("No suppliers found")
                                                        : t('Start typing to search...')
                                                }
                                                isClearable={true}
                                                isLoading={loading}
                                                value={supplier}
                                                theme={(theme) => ({
                                                    ...theme,
                                                    colors: {
                                                        ...theme.colors,
                                                        primary25: isDark ? "#373737" : "#e6f7ffff",

                                                    },
                                                })}
                                            />
                                            {errors.supplier && (
                                                <Form.Text className="text-danger">{errors.supplier}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    {/* Items Selection */}
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                {t("Select Items")}
                                                <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                hideSelectedOptions={false}
                                                options={getSelectOptions}
                                                value={getSelectedValues}
                                                onChange={handleItemSelection}
                                                placeholder={t("Search and select items...")}
                                                onInputChange={(value) => setSearchQueryItem(value)}
                                                components={{ Option }}
                                                styles={customStyles}
                                                classNamePrefix="react-select"
                                                isClearable={true}
                                                isLoading={isLoadingItems}
                                                filterOption={null} // Disable built-in filtering since we handle it
                                                loadingMessage={() => t("Loading items...")}
                                                noOptionsMessage={() =>
                                                    searchQueryItem
                                                        ? t("No items found matching your search")
                                                        : t("Type to search items...")
                                                }
                                            />
                                            <div className="mt-1 text-muted small">
                                                {t("Showing")} {filterItems.length} {t("items")}
                                                {selectedItems.length > 0 && ` | ${t("Selected")}: ${selectedItems.length}`}
                                            </div>
                                            {errors.items && (
                                                <Form.Text className="text-danger">{errors.items}</Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Selected Items Table */}
                                {selectedItems.length > 0 && (
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <div className="border rounded p-3">
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <h6 className="mb-0">
                                                        {t("Selected Items")}
                                                        <span className="badge bg-primary ms-2">{selectedItems.length}</span>
                                                    </h6>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => setSelectedItems([])}
                                                    >
                                                        <i className="fa fa-trash me-1"></i> {t("Clear All")}
                                                    </button>
                                                </div>
                                                <div className="table-responsive">
                                                    <Table bordered hover size="sm">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th width="5%">#</th>
                                                                <th width="20%">{t("Product")}</th>
                                                                <th width="15%">{t("Manufacturer")}</th>
                                                                <th width="12%">{t("Form")}</th>
                                                                <th width="12%">{t("Package")}</th>
                                                                <th width="10%">{t("Quantity")}</th>
                                                                <th width="15%">{t("Notes")}</th>
                                                                <th width="5%">{t("Action")}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedItems.map((item, index) => (
                                                                <tr key={item.id}>
                                                                    <td>{index + 1}</td>
                                                                    <td>
                                                                        <div>
                                                                            <strong className="d-block">{item.brandName}</strong>
                                                                            <small className="text-muted">{item.saltName}</small>
                                                                        </div>
                                                                    </td>
                                                                    <td><small>{item.manufacturer}</small></td>
                                                                    <td><small>{item.productForm}</small></td>
                                                                    <td><small>{item.package}</small></td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="1"
                                                                            value={item.quantity}
                                                                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                                            className="form-control-sm"
                                                                            style={{ width: "80px" }}
                                                                            {...disableScrollProps}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="text"
                                                                            value={item.notes}
                                                                            onChange={(e) => handleNotesChange(item.id, e.target.value)}
                                                                            placeholder={t("Notes")}
                                                                            size="sm"
                                                                            className="form-control-sm"
                                                                        />
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <button
                                                                            className="btn btn-sm btn-danger"
                                                                            onClick={() => handleRemoveItem(item.id)}
                                                                            id="DeleteToolTip"
                                                                        >
                                                                            <i className="fa fa-trash"></i>
                                                                        </button>
                                                                        <ToolTip
                                                                            attrToolTip={{
                                                                                placement: 'top',
                                                                                isOpen: deleteTooltipOpen,
                                                                                target: 'DeleteToolTip',
                                                                                toggle: toggleDeleteTooltip,
                                                                            }}
                                                                        >
                                                                            <strong>{t("Remove")}</strong>
                                                                        </ToolTip>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                {/* Submit Buttons */}
                                <Row className="g-3">
                                    <Col md={12}>
                                        <div className="d-flex gap-2 justify-content-end">
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                disabled={loading || !supplier || selectedItems.length === 0}
                                            >
                                                {loading ? t("Creating...") : t("Create Pre-Order")}
                                                {selectedItems.length > 0 && (
                                                    <span className="badge bg-white text-primary ms-2">
                                                        {selectedItems.length}
                                                    </span>
                                                )}
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={gotoList}
                                                disabled={loading}
                                            >
                                                {t("Cancel")}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default AddPreOrder;